import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import PedWorkspaceGuard from '@/components/shared/PedWorkspaceGuard';
import { Button, Select } from '@/components/ui/daisy';
import Fieldset from '@/components/ui/daisy/fieldset';
import { useOrderingAnswers } from '@/data/hook/question/answers/useOrderingAnswers';
import { FlowColumn } from '@/models/Question';
import { PlusIcon } from '@heroicons/react/24/outline';
import { twJoin } from 'tailwind-merge';
import { OrderingFormFields } from './OrderingForm';
import OrderingItem from './OrderingItem';
import usePedWorkspace from '@/data/hook/ped-content/usePedWorkspace';

export default function OrderingItemsForm() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab.createQuestionModal.orderingForm',
  });
  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab.createQuestionModal.answersCommon',
  });

  const { watch, register } = useFormContext<OrderingFormFields>();
  const flow = watch('flow');
  const liveItems = watch('orderingItems');

  const {
    fields,
    error,
    handleAdd,
    handleUpdateText,
    handleUpdateMedia,
    handleUpdateValidPositions,
    handleMarkIncorrect,
    handleDelete,
    isNewItem,
  } = useOrderingAnswers();

  const containerClassName = twJoin(
    'flex gap-6',
    flow === FlowColumn.HORIZONTAL ? 'flex-wrap' : 'flex-col',
  );

  const { isLimitedEdition } = usePedWorkspace();

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h3 className="text-base font-semibold">{tCommon('answersTitle')}</h3>
        <ConditionalRenderer condition={!!error?.root?.message} fallback={null}>
          <Fieldset.Label className="text-error">
            {error?.root?.message}
          </Fieldset.Label>
        </ConditionalRenderer>
      </div>

      <Select.Fieldset label={t('orientation')}>
        <Select {...register('flow')} className="w-full">
          <Select.Option value={FlowColumn.HORIZONTAL}>
            {t('horizontal')}
          </Select.Option>
          <Select.Option value={FlowColumn.VERTICAL}>
            {t('vertical')}
          </Select.Option>
        </Select>
      </Select.Fieldset>

      <PedWorkspaceGuard>
        <Button
          type="button"
          variant="soft"
          color="primary"
          className="self-start"
          onClick={handleAdd}
          disabled={isLimitedEdition}
        >
          <PlusIcon className="size-3.5" />
          {tCommon('addOption')}
        </Button>
      </PedWorkspaceGuard>

      <div className={containerClassName}>
        {fields.map((field, index) => (
          <OrderingItem
            key={field.fieldId}
            item={liveItems?.[index] ?? field}
            index={index}
            totalItems={fields.length}
            flow={flow}
            initialEditing={isNewItem({
              index,
            })}
            onUpdateText={text => handleUpdateText(index, text)}
            onUpdateMedia={(file, altText) =>
              handleUpdateMedia(index, file, altText)
            }
            onUpdateValidPositions={positions =>
              handleUpdateValidPositions(index, positions)
            }
            onMarkIncorrect={() => handleMarkIncorrect(index)}
            onDelete={() => handleDelete(index)}
          />
        ))}
      </div>
    </div>
  );
}
